import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SearchPanel } from "@/components/SearchPanel";
import { StatsCards } from "@/components/StatsCards";
import { ResultsTable } from "@/components/ResultsTable";
import { SearchHistory } from "@/components/SearchHistory";
import { BrandLogo } from "@/components/BrandLogo";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { MapPinned, TriangleAlert, SearchX, Globe2, PanelLeftClose, PanelLeftOpen, LogOut } from "lucide-react";
import { toast } from "sonner";
import {
  runSearch,
  listSearches,
  getSearch,
  deleteSearch,
  downloadExcel,
  findEmails,
  findOwners,
  runAudit,
  runReviewAnalysis,
  createReportLink,
  createGridRank,
  updateManualGbpReview,
  analyzeManualGbpText,
  suggestPhotoCategories,
  gatherWideReportData,
  generateWideReport,
  updateWideReportNarrative,
  approveWideReport,
  gatherContentOpportunities,
  generateContentRecommendations,
  updateConsent,
  updateStatus,
  bulkUpdateStatus,
  clearAdminToken,
  completeReport,
  reportUrl,
  prepareOutreach,
  sendOutreach,
} from "@/lib/api";
import { grantConsulting } from "@/lib/adminApi";
import { TESTIDS } from "@/constants/testIds";
import useNoindex from "@/lib/useNoindex";

const ERROR_COPY = {
  invalid_api_key:
    "API anahtarı geçersiz veya yetkisiz. Google Cloud'da 'Places API (New)' etkin ve fatura hesabı bağlı olmalı.",
  quota_exceeded: "Google kotası aşıldı. Lütfen biraz sonra tekrar deneyin.",
  network: "Bağlantı hatası. İnternet bağlantınızı kontrol edin.",
  google_error: "Google Places isteği başarısız oldu. Lütfen tekrar deneyin.",
};

export default function Dashboard() {
  useNoindex();
  const navigate = useNavigate();
  const handleLogout = () => {
    clearAdminToken();
    navigate("/login", { replace: true });
  };
  const [history, setHistory] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [results, setResults] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlyLeads, setOnlyLeads] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [findingEmails, setFindingEmails] = useState(false);
  const [findingOwners, setFindingOwners] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [analysingReviews, setAnalysingReviews] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Collapse the search panel once results land — the table is the point
  // then, and this is what removes the need to scroll right to see it all.
  // Keyed on activeId (a new search or reopened history entry), not on
  // `results` itself, so in-place row updates (status change, audit, etc.)
  // never yank the panel closed under the user.
  useEffect(() => {
    if (activeId) setSidebarOpen(false);
  }, [activeId]);

  const refreshHistory = useCallback(async () => {
    try {
      const data = await listSearches();
      setHistory(data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  const handleSearch = async (payload) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setSummary(null);
    setBenchmark(null);
    setOnlyLeads(false);
    try {
      const data = await runSearch(payload);
      setResults(data.results);
      setSummary(data.summary);
      setActiveId(data.id);
      await refreshHistory();
      if (data.summary.total === 0) {
        toast.info("Sonuç bulunamadı", {
          description: "Farklı bir şehir/kategori deneyin.",
        });
      } else {
        toast.success(`${data.summary.total} işletme bulundu`, {
          description: `${data.summary.leads} tanesinin web sitesi yok (lead).`,
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code || "google_error";
      setError({ code, message: ERROR_COPY[code] || detail?.message || "Bilinmeyen hata." });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenHistory = async (id) => {
    setLoading(true);
    setError(null);
    setOnlyLeads(false);
    try {
      const data = await getSearch(id);
      setResults(data.results);
      setSummary(data.summary);
      setBenchmark(data.benchmark || null);
      setActiveId(data.id);
    } catch (e) {
      toast.error("Arama açılamadı");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async (id) => {
    try {
      await deleteSearch(id);
      toast.success("Arama silindi");
      if (activeId === id) {
        setResults(null);
        setSummary(null);
        setActiveId(null);
      }
      await refreshHistory();
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const handleExport = async (filter) => {
    if (!activeId) return;
    setExporting(filter);
    try {
      await downloadExcel(activeId, filter);
      toast.success("Excel indirildi", {
        description: filter === "no_website" ? "Sadece lead'ler" : "Tüm sonuçlar",
      });
    } catch (e) {
      toast.error("Excel oluşturulamadı");
    } finally {
      setExporting(false);
    }
  };

  const handleFindEmails = async () => {
    if (!activeId) return;
    setFindingEmails(true);
    toast.info("E-postalar aranıyor…", {
      description: "İşletme web sitelerinden (Impressum/Kontakt) e-posta toplanıyor.",
    });
    try {
      const data = await findEmails(activeId);
      setResults(data.results);
      if (data.found > 0) {
        toast.success(`${data.found} e-posta bulundu`, {
          description: `${data.scanned} web sitesi tarandı.`,
        });
      } else {
        toast.info("E-posta bulunamadı", {
          description: `${data.scanned} site tarandı, iletişim e-postası tespit edilemedi.`,
        });
      }
    } catch (e) {
      toast.error("E-posta bulma başarısız oldu");
    } finally {
      setFindingEmails(false);
    }
  };

  const handleFindOwners = async () => {
    if (!activeId) return;
    setFindingOwners(true);
    toast.info("İşletme sahipleri aranıyor…", {
      description: "Web sitelerinin Impressum sayfasından isim toplanıyor.",
    });
    try {
      const data = await findOwners(activeId);
      setResults(data.results);
      if (data.found > 0) {
        toast.success(`${data.found} işletme sahibi bulundu`, {
          description: `${data.scanned} web sitesi tarandı.`,
        });
      } else {
        toast.info("İşletme sahibi bulunamadı", {
          description: `${data.scanned} site tarandı, Impressum'da isim tespit edilemedi.`,
        });
      }
    } catch (e) {
      toast.error("İşletme sahibi bulma başarısız oldu");
    } finally {
      setFindingOwners(false);
    }
  };

  const handleAudit = async () => {
    if (!activeId) return;
    setAuditing(true);
    toast.info("Dijital varlık taranıyor…", {
      description: "Her firmanın sitesi kontrol ediliyor (API maliyeti yok).",
    });
    try {
      const data = await runAudit(activeId);
      setResults(data.results);
      setBenchmark(data.benchmark);
      toast.success(`${data.audited} firma tarandı`, {
        description: `Kohort puan medyanı: ${data.benchmark.score_median}/100 · yorum medyanı: ${data.benchmark.review_median}`,
      });
    } catch (e) {
      toast.error("Tarama başarısız oldu");
    } finally {
      setAuditing(false);
    }
  };

  const handleReviewAnalysis = async (placeIds) => {
    if (!activeId) return;
    setAnalysingReviews(true);
    toast.info("Vitrin yorumları analiz ediliyor…", {
      description: "Google her profil için yalnızca 5 öne çıkan yorum veriyor.",
    });
    try {
      const data = await runReviewAnalysis(activeId, placeIds);
      setResults(data.results);
      toast.success(`${data.analysed} firmanın yorumları analiz edildi`);
    } catch (e) {
      toast.error("Yorum analizi başarısız oldu");
    } finally {
      setAnalysingReviews(false);
    }
  };

  const handleStatusChange = async (placeId, status) => {
    if (!activeId) return;
    // optimistic update
    setResults((prev) =>
      prev ? prev.map((b) => (b.place_id === placeId ? { ...b, status } : b)) : prev
    );
    try {
      await updateStatus(activeId, placeId, status);
    } catch (e) {
      toast.error("Durum güncellenemedi");
      refreshHistory();
    }
  };

  const handleConsentChange = async (placeId, consent) => {
    if (!activeId) return;
    setResults((prev) =>
      prev ? prev.map((b) => (b.place_id === placeId ? { ...b, consent } : b)) : prev
    );
    try {
      await updateConsent(activeId, placeId, consent);
    } catch (e) {
      toast.error("Onay durumu güncellenemedi");
      refreshHistory();
    }
  };

  const handleCreateReport = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await createReportLink(activeId, business.place_id);
    setResults((prev) =>
      prev
        ? prev.map((b) => (b.place_id === business.place_id ? data.business : b))
        : prev
    );
    return {
      url: reportUrl(data.token),
      top3: data.top3 || [],
      readiness: data.readiness || null,
      estimatedCostUsd: data.estimated_cost_usd ?? null,
    };
  };

  const handleCompleteReport = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await completeReport(activeId, business.place_id);
    setResults((prev) =>
      prev ? prev.map((b) => (b.place_id === business.place_id ? data.business : b)) : prev
    );
    return {
      url: reportUrl(data.token),
      top3: data.top3 || [],
      readiness: data.readiness,
      estimatedCostUsd: data.estimated_cost_usd,
    };
  };

  const handleUpdateManualGbpReview = async (business, checks) => {
    if (!activeId) throw new Error("no active search");
    const data = await updateManualGbpReview(activeId, business.place_id, checks);
    setResults((prev) =>
      prev
        ? prev.map((b) => (b.place_id === business.place_id ? data.business : b))
        : prev
    );
    return data.business;
  };

  const handleAnalyzeGbpText = async (business, kind, text) => {
    if (!activeId) throw new Error("no active search");
    const data = await analyzeManualGbpText(activeId, business.place_id, kind, text);
    setResults((prev) =>
      prev
        ? prev.map((b) => (b.place_id === business.place_id ? data.business : b))
        : prev
    );
    return data;
  };

  const handleSuggestPhotoCategories = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await suggestPhotoCategories(activeId, business.place_id);
    setResults((prev) =>
      prev
        ? prev.map((b) => (b.place_id === business.place_id ? data.business : b))
        : prev
    );
    return data;
  };

  const handleCreateGridRank = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await createGridRank(activeId, business.place_id);
    setResults((prev) =>
      prev
        ? prev.map((b) =>
            b.place_id === business.place_id ? { ...b, grid_rank: data.grid_rank } : b
          )
        : prev
    );
    return data.grid_rank;
  };

  // Shared by all four wide-report actions: each backend endpoint returns
  // the full { wide_report } for the business, so the merge is identical.
  const mergeWideReport = (placeId, wideReport) => {
    setResults((prev) =>
      prev
        ? prev.map((b) =>
            b.place_id === placeId ? { ...b, wide_report: wideReport } : b
          )
        : prev
    );
  };

  const handleGatherWideReport = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await gatherWideReportData(activeId, business.place_id);
    mergeWideReport(business.place_id, data.wide_report);
    return data.wide_report;
  };

  const handleGenerateWideReport = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await generateWideReport(activeId, business.place_id);
    mergeWideReport(business.place_id, data.wide_report);
    return data.wide_report;
  };

  const handleUpdateWideReportNarrative = async (business, narrative) => {
    if (!activeId) throw new Error("no active search");
    const data = await updateWideReportNarrative(activeId, business.place_id, narrative);
    mergeWideReport(business.place_id, data.wide_report);
    return data.wide_report;
  };

  const handleApproveWideReport = async (business, force = false) => {
    if (!activeId) throw new Error("no active search");
    const data = await approveWideReport(activeId, business.place_id, force);
    mergeWideReport(business.place_id, data.wide_report);
    return data.wide_report;
  };

  const handleGatherContentOpportunities = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await gatherContentOpportunities(activeId, business.place_id);
    mergeWideReport(business.place_id, data.wide_report);
    return data.wide_report;
  };

  const handleGenerateContentRecommendations = async (business) => {
    if (!activeId) throw new Error("no active search");
    const data = await generateContentRecommendations(activeId, business.place_id);
    mergeWideReport(business.place_id, data.wide_report);
    return data.wide_report;
  };

  // Errors (e.g. 409 free quota exhausted) propagate to ConsultingControl,
  // which shows the API detail inline.
  const handleGrantConsulting = async (business, source) => {
    if (!activeId) throw new Error("no active search");
    const data = await grantConsulting(activeId, business.place_id, source);
    setResults((prev) =>
      prev ? prev.map((b) => (b.place_id === business.place_id ? { ...b, consulting: data.consulting } : b)) : prev
    );
    toast.success("Danışmanlık verildi");
    return data.consulting;
  };

  const handleBulkStatus = async (placeIds, status) => {
    if (!activeId) return;
    const idSet = new Set(placeIds);
    // optimistic update
    setResults((prev) =>
      prev ? prev.map((b) => (idSet.has(b.place_id) ? { ...b, status } : b)) : prev
    );
    await bulkUpdateStatus(activeId, placeIds, status);
  };

  const handlePrepareOutreach = async (placeIds) => {
    if (!activeId) throw new Error("no active search");
    return await prepareOutreach(activeId, placeIds);
  };

  const handleSendOutreach = async (payload) => {
    if (!activeId) throw new Error("no active search");
    return await sendOutreach(activeId, payload);
  };

  return (
    <div className="min-h-screen bg-background app-noise">
      {/* Header */}
      <header
        data-testid={TESTIDS.appHeader}
        className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70"
      >
        <div className="mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div data-testid={TESTIDS.appBrand}>
            <BrandLogo className="h-11" />
          </div>
          <div className="flex items-center gap-3">
            <div
              data-testid={TESTIDS.regionIndicator}
              className="hidden sm:flex items-center gap-2 rounded-full border bg-secondary px-3 py-1.5 text-xs font-medium"
            >
              <Globe2 className="h-3.5 w-3.5 text-primary" />
              Google Places API
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary"
              title="Çıkış yap"
            >
              <LogOut className="h-3.5 w-3.5" />
              Çıkış
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8 py-6">
        <div
          className={`grid grid-cols-1 gap-6 transition-[grid-template-columns] duration-200 ${
            sidebarOpen ? "lg:grid-cols-[340px_1fr]" : "lg:grid-cols-[0px_1fr]"
          }`}
        >
          {/* Left */}
          <div className={sidebarOpen ? "space-y-6" : "hidden lg:block lg:space-y-6 lg:overflow-hidden"}>
            <SearchPanel onSearch={handleSearch} loading={loading} />
            <SearchHistory
              history={history}
              activeId={activeId}
              onOpen={handleOpenHistory}
              onDelete={handleDeleteHistory}
            />
          </div>

          {/* Right */}
          <div className="space-y-5 min-w-0">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border bg-card px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary"
              data-testid="sidebar-toggle"
              title={sidebarOpen ? "Arama panelini gizle" : "Arama panelini göster"}
            >
              {sidebarOpen ? <PanelLeftClose className="h-3.5 w-3.5" /> : <PanelLeftOpen className="h-3.5 w-3.5" />}
              {sidebarOpen ? "Paneli Gizle" : "Yeni Arama / Geçmiş"}
            </button>
            {error && (
              <Alert variant="destructive" data-testid={`error-${error.code}`}>
                <TriangleAlert className="h-4 w-4" />
                <AlertTitle>Bir sorun oluştu</AlertTitle>
                <AlertDescription>{error.message}</AlertDescription>
              </Alert>
            )}

            {loading && (
              <div className="space-y-5" data-testid={TESTIDS.loadingState}>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-[76px] rounded-xl" />
                  ))}
                </div>
                <Skeleton className="h-10 w-full rounded-lg" />
                <div className="space-y-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-11 w-full rounded-md" />
                  ))}
                </div>
              </div>
            )}

            {!loading && !error && results === null && (
              <div
                data-testid={TESTIDS.emptyState}
                className="hero-gradient rounded-2xl border border-dashed p-10 sm:p-16 text-center"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-card border shadow-sm">
                  <MapPinned className="h-8 w-8 text-primary" />
                </div>
                <h2 className="mt-5 font-heading text-2xl font-bold">
                  Almanya'daki işletmeleri keşfet
                </h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground leading-relaxed">
                  Soldaki panelden bir arama yap. Örneğin{" "}
                  <span className="font-medium text-foreground">"Berlin lastik tamircisi"</span>{" "}
                  veya şehir + kategori olarak <span className="font-medium text-foreground">Berlin + Kuaför</span>.
                  Web sitesi olmayan işletmeler otomatik olarak <strong>LEAD</strong> işaretlenir.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full border bg-card px-3 py-1">🔍 2 arama modu</span>
                  <span className="rounded-full border bg-card px-3 py-1">🎯 Otomatik lead tespiti</span>
                  <span className="rounded-full border bg-card px-3 py-1">📊 Excel export</span>
                </div>
              </div>
            )}

            {!loading && !error && results !== null && summary?.total === 0 && (
              <div
                data-testid={TESTIDS.zeroResults}
                className="rounded-2xl border border-dashed p-12 text-center"
              >
                <SearchX className="mx-auto h-10 w-10 text-muted-foreground" />
                <h3 className="mt-4 font-heading text-lg font-semibold">Sonuç bulunamadı</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Farklı bir şehir veya kategori deneyin.
                </p>
              </div>
            )}

            {!loading && !error && results !== null && summary?.total > 0 && (
              <div className="space-y-5 fade-in">
                <StatsCards summary={summary} />
                <ResultsTable
                  results={results}
                  onlyLeads={onlyLeads}
                  setOnlyLeads={setOnlyLeads}
                  onExport={handleExport}
                  exporting={exporting}
                  onFindEmails={handleFindEmails}
                  findingEmails={findingEmails}
                  onFindOwners={handleFindOwners}
                  findingOwners={findingOwners}
                  onAudit={handleAudit}
                  auditing={auditing}
                  onReviewAnalysis={handleReviewAnalysis}
                  analysingReviews={analysingReviews}
                  benchmark={benchmark}
                  onStatusChange={handleStatusChange}
                  onConsentChange={handleConsentChange}
                  onCreateReport={handleCreateReport}
                  onCreateGridRank={handleCreateGridRank}
                  onUpdateManualGbpReview={handleUpdateManualGbpReview}
                  onAnalyzeGbpText={handleAnalyzeGbpText}
                  onSuggestPhotoCategories={handleSuggestPhotoCategories}
                  onGatherWideReport={handleGatherWideReport}
                  onGenerateWideReport={handleGenerateWideReport}
                  onGatherContentOpportunities={handleGatherContentOpportunities}
                  onGenerateContentRecommendations={handleGenerateContentRecommendations}
                  onUpdateWideReportNarrative={handleUpdateWideReportNarrative}
                  onApproveWideReport={handleApproveWideReport}
                  onGrantConsulting={handleGrantConsulting}
                  onCompleteReport={handleCompleteReport}
                  onBulkStatus={handleBulkStatus}
                  onPrepareOutreach={handlePrepareOutreach}
                  onSendOutreach={handleSendOutreach}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
